import { Card } from '@design/components/card/Card';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { usePulseBigMovers } from '@terminal/react-query-hooks/themes/use-pulse-table/usePulseTables';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { PulseRankedTable } from '@terminal/views/theme-overview/tab-content/pulse-overview/components/pulse-tables/PulseRankedTable';
import {
  renderPulseBigMoverChange,
  toPulseBigMoverRankedRows,
} from '@terminal/views/theme-overview/tab-content/pulse-overview/components/pulse-tables/pulseBigMoverRows';
import { useTranslation } from 'react-i18next';

import styles from './CompanyRanking.module.scss';

export interface CompanyRankingProps {
  themeId: string;
}

export const CompanyRanking = ({ themeId }: CompanyRankingProps) => {
  const { t } = useTranslation(['widget']);
  const { navigateToAssetOverview } = useNavigateToAssetOverview();
  const bigMovers = usePulseBigMovers({ themeId });

  const openAssetOverview = (entityTag: string) => {
    navigateToAssetOverview(
      entityTag,
      POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW_DRAWER
    );
  };

  const movers = bigMovers.data ?? [];
  const winners = toPulseBigMoverRankedRows(
    movers.filter(mover => mover.changePercent > 0)
  );
  const losers = toPulseBigMoverRankedRows(
    movers.filter(mover => mover.changePercent < 0)
  );

  const renderSkeletonLoader = () => (
    <SkeletonLoader
      className={styles.tableCard}
      areas={[
        {
          styles: { width: '100%', height: '172px', borderRadius: '8px' },
        },
      ]}
    />
  );

  return (
    <section
      className={styles.root}
      data-testid="industry-drawer-company-ranking"
    >
      {bigMovers.isLoading ? (
        <>
          {renderSkeletonLoader()}
          {renderSkeletonLoader()}
        </>
      ) : (
        <>
          <ErrorBoundaryWrapper showBorder={false}>
            <Card
              className={styles.tableCard}
              aria-label={t('widget:themeOverview.pulse.companyRankingWinners')}
            >
              <PulseRankedTable
                rows={winners}
                lastColumnLabel={t('widget:themeOverview.pulse.changePercent')}
                lastCellIsNumber
                data-testid="industry-drawer-company-ranking-winners"
                onRowClick={row => openAssetOverview(row.company.entityTag)}
                renderLastCell={renderPulseBigMoverChange}
              />
            </Card>
          </ErrorBoundaryWrapper>
          <ErrorBoundaryWrapper showBorder={false}>
            <Card
              className={styles.tableCard}
              aria-label={t('widget:themeOverview.pulse.companyRankingLosers')}
            >
              <PulseRankedTable
                rows={losers}
                lastColumnLabel={t('widget:themeOverview.pulse.changePercent')}
                lastCellIsNumber
                data-testid="industry-drawer-company-ranking-losers"
                onRowClick={row => openAssetOverview(row.company.entityTag)}
                renderLastCell={renderPulseBigMoverChange}
              />
            </Card>
          </ErrorBoundaryWrapper>
        </>
      )}
    </section>
  );
};
